import React, { useCallback, useEffect, useReducer, useRef, useState, Fragment } from 'react';

import { selectMediaConfig } from 'store/media/selector';
import { REQUEST_FROM } from 'store/reviewList/types';

import { Divider } from '@travel/design-system/core/src/components/Divider';
import ProviderCommonHeader from '@travel/design-system/core/src/components/ProviderCommonHeader';
import { MENU_PAGE_CODE } from '@travel/design-system/core/src/components/ProviderCommonHeader/constants';
import ProviderCommonHeaderSkeleton from '@travel/design-system/core/src/components/ProviderCommonHeader/providerCommonHeaderSkeleton';
import { getL10nNumber } from '@travel/i18n';
import { ArrowUp } from '@travel/icons/ui';
import { Translate } from '@travel/translation';
import { useRat } from '@travel/traveler-core/components/DataLayer/utils';
import { useDeviceTypes, useDialogHandler } from '@travel/traveler-core/hooks';
import { FlatButton, IconTextLink } from '@travel/ui';
import LoadingCircle from '@travel/ui/components/FlatButton/loadingCircle';
import { cx, isEmptyArray, isEmptyObject, parseQueryString } from '@travel/utils';

import LoadMoreButton from 'components/LoadMoreButton';
import MediaExpandedView from 'components/MediaExpandedView/index';
import MediaList from 'components/MediaList';
import NoReviewsIllustration from 'components/NoReviewsIllustration';
import SearchArea from 'components/SearchArea';
import { getProviderPath } from 'components/SearchArea/utils/providerPath';
import UserNotFoundIllustration from 'components/UserNotFoundIllustration';
import { ratingMapping } from 'constants/providerReview';
import { RAT_DATED_URL, RAT_SMART_DATED, RAT_UNDATED_URL } from 'constants/ratAnalytics';
import { initialState, mediaListReducer, MediaListContext } from 'context/mediaListContext';
import { RatTagProvider } from 'context/ratTagContext';
import { useHistory } from 'core/universalRouter/hooks/useHistory';
import paths from 'core/universalRouter/paths';
import useLogin from 'hooks/useLogin';
import useScroll from 'hooks/useScroll';
import useSharedAdobeTag from 'hooks/useSharedAdobeTag';
import useViewCount from 'hooks/useViewCount';
import { getIsLoggedIn } from 'store/authCode/selectors';
import { fetchAndAppendToMediaGallery } from 'store/mediaGallery/actions';
import {
  getIsAppendFetching as getIsMediaGalleryAppendFetching,
  getIsFetchingMedia,
  getMediaGalleryData,
} from 'store/mediaGallery/selector';
import { addToFavoriteList, deleteFromFavoriteList } from 'store/providerCommonHeader/actions';
import {
  selectBookmarkErrorId,
  selectIsBookmarked,
  selectIsBookmarkError,
  selectIsBookmarkLimitError,
  selectIsFetchingProviderHeaderInfo,
  selectIsJRDP,
  selectJrdpQuery,
  selectProviderHeaderInfo,
  selectRtCond,
  selectRtCondEx,
} from 'store/providerCommonHeader/selectors';
import {
  getAiSummary,
  getIsFetchingProviderSummary,
  getProviderSummary,
} from 'store/providerSummary/selector';
import { fetchReviewList } from 'store/reviewList/actions';
import { getIsFetchingReviewList, getMergedReviewList } from 'store/reviewList/selector';
import {
  generateABTestRatParameters,
  AB_TESTING_VALUE,
  ACTION_LIKE,
  PROVIDER_REVIEW_PAGE,
} from 'utils/ratHelper';
import { getDefaultURLSearchRequest } from 'utils/reviewHelper';
import { scrollToTop } from 'utils/scrollToTop';
import { HEADER_LABEL } from './commonHeaderLabel';
import MediaGallery from './components/MediaGallery';
import ProviderReviewCard from './components/ProviderReviewCard';
import ProviderReviewSkeleton, { ReviewListSkeleton } from './components/ProviderReviewSkeleton';
import ProviderSummary from './components/ProviderSummary';
import ScoresSection from './components/ScoresSection';

import { pushLocation } from 'store/__router/actions';

import { LANGUAGE_CODE, REVIEW_HOSTNAME, REVIEW_LIST_LOAD_MORE_LIMIT } from 'constants/common';
import { MEDIA_GALLERY_LIMIT } from 'constants/media';
import { useAppDispatch, useAppSelector } from 'store/hooks';

import styles from './providerReview.module.scss';

import type { URLSearchQuery } from 'core/types/ReviewList';
export type QueryParamProps = {
  query: URLSearchQuery;
  params: { providerId: string };
};

const ProviderReview = (props: QueryParamProps) => {
  const [state, dispatch] = useReducer(mediaListReducer, {
    ...initialState,
    shouldHideNonOKStatusMedia: true,
  });
  const appDispatch = useAppDispatch();

  const { query, params } = props;

  const providerHeaderInfo = useAppSelector(selectProviderHeaderInfo);
  const rtCond = useAppSelector(selectRtCond);
  const rtCondEx = useAppSelector(selectRtCondEx);
  const { dated } = rtCondEx;

  const isJRDP = useAppSelector(selectIsJRDP);
  const jrdpQuery = useAppSelector(selectJrdpQuery);
  const isBookmarked = useAppSelector(selectIsBookmarked);
  const isBookmarkError = useAppSelector(selectIsBookmarkError);
  const isBookmarkLimitError = useAppSelector(selectIsBookmarkLimitError);
  const bookmarkErrorId = useAppSelector(selectBookmarkErrorId);
  const { onClickLogin } = useLogin();
  const isLogin = useAppSelector(getIsLoggedIn);
  const history = useHistory();
  const historyParams = history
    ? getDefaultURLSearchRequest(parseQueryString(history.location.search))
    : getDefaultURLSearchRequest();
  const aiSummary = useAppSelector(getAiSummary);
  const queryParams = {
    ...getDefaultURLSearchRequest(query),
    ...historyParams,
    requestFrom: REQUEST_FROM.PROVIDER_REVIEW,
  };

  const isEnableVideoUpload = useAppSelector(selectMediaConfig);

  const bookmarkButtonAction = useCallback(() => {
    if (!isLogin) {
      const returnUrl = new URL(window?.location.href);

      returnUrl.searchParams.set('favoriteEntityId', params.providerId);
      onClickLogin(returnUrl.toString());

      return;
    }

    if (isBookmarked) {
      appDispatch(deleteFromFavoriteList());
    } else {
      appDispatch(addToFavoriteList());
    }
  }, [appDispatch, isBookmarked, isLogin, onClickLogin, params.providerId]);

  const undoBookmark = useCallback(() => {
    appDispatch(addToFavoriteList());
  }, [appDispatch]);

  const categoryScoreData = useAppSelector(getProviderSummary);
  const isFetchingProviderSummary = useAppSelector(getIsFetchingProviderSummary);
  const isFetchingReviewList = useAppSelector(getIsFetchingReviewList);
  const isFetchingMedia = useAppSelector(getIsFetchingMedia);
  const isFetchingProviderHeaderInfo = useAppSelector(selectIsFetchingProviderHeaderInfo);
  const isFetching = isFetchingProviderSummary || isFetchingReviewList || isFetchingMedia;
  const mergedReviewListData = useAppSelector(getMergedReviewList);
  const { offset = 0, limit = REVIEW_LIST_LOAD_MORE_LIMIT, total } = mergedReviewListData;
  const mergedReviewListDataLength = mergedReviewListData?.contents.length || 0;
  const [pageOffset, setOffset] = useState(offset || 0);
  const [isSearchFormDisplayed, setIsSearchFormDisplayed] = useState(true);
  const pageNumber = Number(query?.page) || 1;
  const providerReviewUrl = paths.providerReview.pathResolver(params.providerId);

  const isDatedCookie = dated || false;

  const breadcrumbsHotelLabel = (
    <Translate
      id="Review_SEO.Provider_Review_List.Breadcrumbs.Provider_Review"
      data={{ hotel_name: providerHeaderInfo?.hotel.hotelName }}
    />
  );

  const targetRef = useRef<Array<Element | null>>([]);

  useViewCount({
    targetRef,
    contents: mergedReviewListData.contents,
  });

  const resetPaginationOffset = () => {
    setOffset(0);
  };

  useRat('A', true);

  const { isPC } = useDeviceTypes();
  const ratPcUrl = isDatedCookie ? RAT_DATED_URL : RAT_UNDATED_URL;

  useSharedAdobeTag(isPC ? ratPcUrl : RAT_SMART_DATED);

  useEffect(() => {
    if (!isFetching) {
      setHideSkeleton(false);
    }
  }, [isFetching]);

  const hasMore = mergedReviewListDataLength < total;

  const isGalleryMediaAppendFetching = useAppSelector(getIsMediaGalleryAppendFetching);
  const [isDisplaySearchArea, setDisplaySearchArea] = useState(false);

  const isFilterResultsEmpty =
    isDisplaySearchArea && isEmptyArray(mergedReviewListData?.contents || []);
  const isNoScore = categoryScoreData && categoryScoreData?.overallScore <= 0;

  const isReviewPresent = mergedReviewListDataLength > 0;

  useEffect(() => {
    if (!isEmptyObject(query) || total > 0) {
      setDisplaySearchArea(true);
    }
  }, [query, total]);

  const galleryData = useAppSelector(getMediaGalleryData);
  const ratTagValues = {
    action: ACTION_LIKE,
    pageName: PROVIDER_REVIEW_PAGE,
  };
  const {
    offset: galleryDataOffset,
    limit: galleryDataLimit,
    total: galleryDataTotal,
    contents: mediaReviews,
  } = galleryData;

  const [isHideSkeleton, setHideSkeleton] = useState(false);
  const handlePagination = async () => {
    const newOffset = pageOffset + limit;

    setHideSkeleton(true);
    appDispatch(
      fetchReviewList({
        ...queryParams,
        offset: newOffset,
        limit,
        requestFrom: REQUEST_FROM.PROVIDER_REVIEW,
        providerId: params.providerId,
      }),
    );
    setOffset(newOffset);
  };

  const getMappedRatings = (ratings) => {
    return ratings.map((item) => {
      const star = ratingMapping[item.rating];

      return {
        stars: star || 0,
        count: item.count || 0,
      };
    });
  };

  const {
    isOpen: isMediaExpandedViewOpen,
    onOpen: onMediaExpandedViewOpen,
    onClose: onMediaExpandedViewClose,
  } = useDialogHandler(false);
  const {
    isOpen: isFullMediaGalleryVisible,
    onOpen: onFullMediaGalleryOpen,
    onClose: onFullMediaGalleryClose,
  } = useDialogHandler(false);

  // 690 is an estimation of header + overview + search form height
  const STICK_HEIGHT = 690;

  const isAboveStickHeight =
    typeof window !== 'undefined' ? window.pageYOffset < STICK_HEIGHT : true;
  // scroll handler
  const scrollYRef = useRef<number>(0);
  const onScroll = useCallback(() => {
    setIsSearchFormDisplayed((prev) => {
      if (window.pageYOffset < STICK_HEIGHT) return true;
      if (scrollYRef.current <= window.pageYOffset) return false;
      // 5 is for making animation not too sensitive
      else if (scrollYRef.current > window.pageYOffset + 5) return true;

      return prev;
    });

    scrollYRef.current = window.pageYOffset;
  }, []);

  useEffect(() => {
    if (isPC) {
      scrollYRef.current = window.pageYOffset;
      document.addEventListener('scroll', onScroll);

      return () => {
        document.removeEventListener('scroll', onScroll);
      };
    }
  }, [onScroll, isPC]);

  const resetFilter = () => {
    const defaultQuery = getDefaultURLSearchRequest();

    appDispatch(
      pushLocation(
        getProviderPath(params.providerId, {
          ...defaultQuery,
          ...(isJRDP && {
            jrdp: '1',
            jrdpQuery: jrdpQuery,
          }),
        }),
      ),
    );
  };

  const hasMoreGalleryImagesToCall = galleryDataLimit + galleryDataOffset < galleryDataTotal;
  const SEE_MORE_DISPLAY_COUNT_THRESHOLD = MEDIA_GALLERY_LIMIT;

  const shouldShowAllSkeleton = history?.location.state?.shouldUpdate ?? true;

  const { isUp } = useScroll();

  if (isFetchingProviderHeaderInfo || !providerHeaderInfo) {
    return (
      <>
        <ProviderCommonHeaderSkeleton isPC={isPC} />
        <div className={styles.skeletonLoader} data-testid="providerReview-skeleton">
          <ProviderReviewSkeleton />
        </div>
      </>
    );
  }

  if (
    (!isFetchingProviderHeaderInfo && isFetching && !isHideSkeleton && shouldShowAllSkeleton) ||
    !categoryScoreData
  ) {
    return (
      <>
        <ProviderCommonHeader
          headerInfo={providerHeaderInfo}
          activeTab={MENU_PAGE_CODE.REVIEW}
          isBookmarked={isBookmarked}
          isBookmarkError={isBookmarkError}
          isBookmarkLimitError={isBookmarkLimitError}
          bookmarkErrorId={bookmarkErrorId}
          bookmarkButtonCallback={bookmarkButtonAction}
          undoBookmark={undoBookmark}
          labels={HEADER_LABEL}
          rtCond={rtCond}
          rtCondEx={rtCondEx}
          isPC={isPC}
          isJRDP={isJRDP}
          jrdpQuery={jrdpQuery}
          languageCode={LANGUAGE_CODE}
          breadcrumbsHotelLabel={breadcrumbsHotelLabel}
          hasSearchBar={isUp && !isAboveStickHeight}
        />
        <div className={styles.skeletonLoader} data-testid="providerReview-skeleton">
          <ProviderReviewSkeleton />
        </div>
      </>
    );
  }

  return (
    <ProviderCommonHeader
      headerInfo={providerHeaderInfo}
      activeTab={MENU_PAGE_CODE.REVIEW}
      isBookmarked={isBookmarked}
      isBookmarkError={isBookmarkError}
      isBookmarkLimitError={isBookmarkLimitError}
      bookmarkErrorId={bookmarkErrorId}
      bookmarkButtonCallback={bookmarkButtonAction}
      undoBookmark={undoBookmark}
      labels={HEADER_LABEL}
      rtCond={rtCond}
      rtCondEx={rtCondEx}
      isPC={isPC}
      isJRDP={isJRDP}
      jrdpQuery={jrdpQuery}
      breadcrumbsHotelLabel={breadcrumbsHotelLabel}
      languageCode={LANGUAGE_CODE}
      hasSearchBar={isUp && !isAboveStickHeight}
    >
      <MediaListContext.Provider value={{ state, dispatch }}>
        <RatTagProvider values={ratTagValues}>
          {isFullMediaGalleryVisible ? (
            <MediaGallery
              mediaReviews={mediaReviews}
              hasMore={hasMoreGalleryImagesToCall}
              onClickMedia={onMediaExpandedViewOpen}
              onClose={onFullMediaGalleryClose}
              isAppendFetching={isGalleryMediaAppendFetching}
              onScrollToBottom={() =>
                appDispatch(
                  fetchAndAppendToMediaGallery({
                    providerId: Number(params.providerId),
                    offset: galleryDataOffset + galleryDataLimit,
                    limit: galleryDataLimit,
                  }),
                )
              }
            />
          ) : (
            <div className={styles.providerReviewWrapper}>
              <div
                className={styles.providerSummaryContainer}
                data-testid="providerReview-container"
              >
                <h1 className={styles.title} data-testid="providerReview-title">
                  <Translate id="Review.GuestReview.Title" />
                </h1>
                <ScoresSection
                  overallScore={categoryScoreData.overallScore}
                  reviewCount={categoryScoreData.reviewCount}
                  starRating={getMappedRatings(categoryScoreData.starRatings)}
                  categoryRating={categoryScoreData.categoryRatings}
                />
                {!isNoScore && aiSummary && (
                  <div className={styles.aiSummaryContainer}>
                    <ProviderSummary AISummary={aiSummary} />
                  </div>
                )}
              </div>
              {isPC && <Divider className={styles.defaultDivider} />}
              {isDisplaySearchArea ? (
                <>
                  <div
                    className={cx(
                      styles.searchAreaWrapper,
                      !isSearchFormDisplayed && styles.hide,
                      isPC && isUp && !isAboveStickHeight && styles.up,
                    )}
                    data-testid="providerReview-searchArea"
                  >
                    <SearchArea
                      params={params}
                      query={queryParams}
                      resetPaginationOffset={resetPaginationOffset}
                      isLoading={isFetching}
                    />
                  </div>
                  {!isPC && <Divider variant={'card'} className={styles.defaultDivider} />}
                  <div className={styles.mediaGallery}>
                    {!isFilterResultsEmpty && galleryDataTotal > 0 && (
                      <>
                        <div className={styles.mediaTitle}>
                          <Translate
                            id={
                              isEnableVideoUpload
                                ? 'Review.ReviewMedia'
                                : 'Review.ReviewMediaNoVideo'
                            }
                          />
                        </div>
                        <MediaList
                          reviewMedia={mediaReviews}
                          layout={isPC ? 'summary' : 'small'}
                          mediaCardClassName={styles.mainGalleryCarouselCard}
                          shouldShowSeeMore={galleryData.total > SEE_MORE_DISPLAY_COUNT_THRESHOLD}
                          limitNumberOfMedia={SEE_MORE_DISPLAY_COUNT_THRESHOLD}
                          onClickMedia={onMediaExpandedViewOpen}
                          onClickMore={onFullMediaGalleryOpen}
                          isDisableLazyLoad={true}
                          key={isPC ? 'PC' : 'SP'}
                        />
                      </>
                    )}
                  </div>
                  {isFetchingReviewList && !isHideSkeleton ? (
                    <div className={styles.skeletonLoader} data-testid="providerReview-skeleton">
                      <ReviewListSkeleton />
                    </div>
                  ) : (
                    <>
                      <div className={styles.reviewCount}>
                        <span>{getL10nNumber(LANGUAGE_CODE, total)}</span>
                        <Translate id="Review_Common.ReviewCount" />
                      </div>
                      {!isFilterResultsEmpty ? (
                        <>
                          <div>
                            {mergedReviewListDataLength &&
                              mergedReviewListData?.contents.map((review, index) => (
                                <Fragment key={review.id}>
                                  <div
                                    ref={(el) => (targetRef.current[index] = el)}
                                    // data-id is added for useViewCount intersection observer
                                    data-id={review.id}
                                  >
                                    <ProviderReviewCard
                                      onClickMedia={onMediaExpandedViewOpen}
                                      reviewData={review}
                                      keywords={queryParams.keywords}
                                      isDisableLazyLoad={index === 0}
                                    />
                                  </div>

                                  <Divider
                                    variant={isPC ? 'default' : 'card'}
                                    className={styles.defaultDivider}
                                  />
                                </Fragment>
                              ))}
                          </div>
                          {hasMore && (
                            <div className={styles.loadMoreContainer}>
                              {isFetchingReviewList && isHideSkeleton ? (
                                <div
                                  className={styles.spinnerWrapper}
                                  data-testid="reviewPage-loading-spinner"
                                >
                                  <LoadingCircle classType="primary" />
                                </div>
                              ) : (
                                <LoadMoreButton
                                  className={styles.loadMoreButton}
                                  customHost={REVIEW_HOSTNAME}
                                  to={providerReviewUrl}
                                  pageNumber={pageNumber}
                                  onClick={handlePagination}
                                />
                              )}
                            </div>
                          )}
                        </>
                      ) : (
                        <div
                          className={styles.noReviewMedia}
                          data-testid="noReviewsWithFilterWrapper"
                        >
                          <NoReviewsIllustration />
                          <div className={styles.noFilterText}>
                            <Translate id="Review.NoReviewWithFilter" />
                          </div>
                          <FlatButton
                            classType="secondary"
                            className={styles.cancelReview}
                            onClick={resetFilter}
                          >
                            <Translate id="Review.ClearFilter" />
                          </FlatButton>
                        </div>
                      )}
                    </>
                  )}
                </>
              ) : (
                <div className={styles.noReviewMedia} data-testid="noReviewsNoScoreWrapper">
                  <UserNotFoundIllustration />
                  <div className={styles.noReviewText}>
                    <Translate id="Review.NoReview" />
                  </div>
                </div>
              )}

              {!isFilterResultsEmpty && isReviewPresent && (
                <div className={styles.gotToTopContainer}>
                  <IconTextLink
                    onClick={scrollToTop}
                    className={styles.gotToTopLabel}
                    icon={<ArrowUp size={16} />}
                    iconPosition="left"
                    text={<Translate id="Review.Web.BackToTop" />}
                    unhoveredColor={'currentColor'}
                    hoveredColor={'currentColor'}
                  />
                </div>
              )}
            </div>
          )}

          <input
            type="hidden"
            name="rat"
            id="ratTestTargeting"
            value={
              mergedReviewListDataLength > 0
                ? JSON.stringify(generateABTestRatParameters(AB_TESTING_VALUE, 'A'))
                : ''
            }
          ></input>

          <MediaExpandedView
            isOpen={isMediaExpandedViewOpen}
            onClose={onMediaExpandedViewClose}
            reviewIndex={state.mediaIndex}
            reviewMediaList={state.galleryMediaList}
          />
        </RatTagProvider>
      </MediaListContext.Provider>
    </ProviderCommonHeader>
  );
};

export default ProviderReview;
